import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import cc from "classcat";
import { useState } from "react";
import { CopyableText } from "@lending/components/CopyableText/CopyableText";
import styles from "../Status.module.css";

interface MembershipShareCardProps {
  productName: string;
  accountNumber?: string;
  routingNumber?: string;
  enrollButtonLabel: string;
  onEnrollNow: () => void;
}

function MembershipShareCard({
  productName,
  accountNumber,
  routingNumber,
  enrollButtonLabel,
  onEnrollNow: onEnrollNow,
}: MembershipShareCardProps) {
  const { l10n } = useLocalization();
  const [showAccountDetails, setShowAccountDetails] = useState(false);

  const accountDetailsContent = showAccountDetails && (
    <div id="membership-share-account-details" className={styles.accountDetails}>
      <div className={styles.accountDetailRow}>
        <span className={styles.accountDetailLabel}>
          {l10n.getString("membership-share-status-account-number", null, "Account number")}
        </span>
        <CopyableText text={accountNumber} className={styles.accountDetailValue}>
          {accountNumber}
        </CopyableText>
      </div>
      <div className={styles.accountDetailRow}>
        <span className={styles.accountDetailLabel}>
          {l10n.getString("membership-share-status-routing-number", null, "Routing number")}
        </span>
        <CopyableText text={routingNumber} className={styles.accountDetailValue}>
          {routingNumber}
        </CopyableText>
      </div>
    </div>
  );

  return (
    <div className={styles.productRow}>
      <div className={styles.productRowTop}>
        <div className={styles.productRowContent}>
          <div className={styles.productInfo}>
            <div className={styles.productName}>{productName}</div>
            <div
              className={styles.viewDetails}
              onClick={() => setShowAccountDetails(!showAccountDetails)}
              role="button"
              tabIndex={0}
              aria-expanded={showAccountDetails}
              aria-controls="membership-share-account-details"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowAccountDetails(!showAccountDetails);
                }
              }}
            >
              <span>
                {l10n.getString(
                  "membership-share-status-view-details",
                  null,
                  "View account details"
                )}
              </span>
              <i
                className={cc([
                  "narmi-icon-chevron-down",
                  styles.viewDetailsIcon,
                  showAccountDetails && styles.viewDetailsIconExpanded,
                ])}
              />
            </div>
          </div>
        </div>
        {/* Desktop button - primary */}
        <div className={cc([styles.productButton, styles.productButtonDesktop])}>
          <Button
            label={enrollButtonLabel}
            onClick={onEnrollNow}
            endIcon="arrow-right"
            kind="primary"
            size="s"
          />
        </div>
      </div>
      {/* Account details - shown on desktop below the row */}
      <div className={styles.accountDetailsDesktop}>{accountDetailsContent}</div>
      {/* Account details - shown on mobile above the button */}
      <div className={styles.accountDetailsMobile}>{accountDetailsContent}</div>
      {/* Mobile button - secondary, below account details */}
      <div className={cc([styles.productButton, styles.productButtonMobile])}>
        <Button
          label={enrollButtonLabel}
          onClick={onEnrollNow}
          endIcon="arrow-right"
          kind="secondary"
          size="xs"
        />
      </div>
    </div>
  );
}

export { MembershipShareCard };
